import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import CategoryButtons from './components/CategoryButtons';
import SpreadsheetHub from './components/SpreadsheetHub';
import PopularCategories from './components/PopularCategories';
import LatestResources from './components/LatestResources';
import BuyingGuides from './components/BuyingGuides';
import PillarArticles from './components/PillarArticles';
import ReviewSection from './components/ReviewSection';
import SafetySection from './components/SafetySection';
import FinalCTA from './components/FinalCTA';

export default function HomePage() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [currentLang, setCurrentLang] = useState(() => {
    const stored = localStorage.getItem('preferred-language');
    if (stored && ['en', 'fr', 'de', 'da'].includes(stored)) return stored;
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'fr', 'de', 'da'].includes(browserLang)) return browserLang;
    return 'en';
  });

  // Auto-detect language from URL path on first load
  useEffect(() => {
    const pathLang = location.pathname.split('/')[1];
    if (pathLang && ['en', 'fr', 'de', 'da'].includes(pathLang) && pathLang !== currentLang) {
      setCurrentLang(pathLang);
    }
  }, [location.pathname]);

  useEffect(() => {
    i18n.changeLanguage(currentLang);
    localStorage.setItem('preferred-language', currentLang);
    document.documentElement.lang = currentLang;
  }, [currentLang, i18n]);

  const handleLanguageChange = (lang: string) => {
    setCurrentLang(lang);
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/^\/[a-z]{2}/, `/${lang}`);
    if (newPath !== currentPath) {
      window.location.href = newPath;
    }
  };

  // Non-home pages (spreadsheet, guides, review, etc.) should have solid navbar
  const isHomePage = location.pathname === '/' || /^\/[a-z]{2}\/?$/.test(location.pathname);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar
        currentLang={currentLang}
        onLanguageChange={handleLanguageChange}
        solid={!isHomePage}
      />
      <main>
        <Hero currentLang={currentLang} />
        <CategoryButtons currentLang={currentLang} />
        <SpreadsheetHub currentLang={currentLang} />
        <PopularCategories currentLang={currentLang} />
        <LatestResources currentLang={currentLang} />
        <BuyingGuides currentLang={currentLang} />
        <PillarArticles currentLang={currentLang} />
        <ReviewSection currentLang={currentLang} />
        <SafetySection currentLang={currentLang} />
        <FinalCTA currentLang={currentLang} />
      </main>
      <Footer currentLang={currentLang} />
    </div>
  );
}